import { Formik, FormikHelpers as FormikActions } from 'formik'
import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router'
import { withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { ConnectionPreference, ValidationError } from 'epc-schema'
import { Form, Indicators, Input, Content } from 'ui'
import { OptionalString, RequiredString } from 'validations'
import { useCurrentProfile, useUpdateProfile } from '../graphql'

/* BEGIN - Form Values */
/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
interface IProfessionalStepTwoFormValues {
  [key: string]: any | string | string[] | undefined
  id?: string
  cities?: string[] | undefined
  connectionPreference?: ConnectionPreference | undefined
  preferredActivities?: string[] | undefined
  gradeLevels?: string[] | undefined
  secondaryLanguages?: string[] | undefined
  otherSecondaryLanguages?: string | string[] | undefined
}

/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const ProfessionalStepTwoSchema = Yup.object().shape({
  cities: Yup.array().of(RequiredString()).min(1, 'Must choose at least 1'),
  connectionPreference: RequiredString(),
  gradeLevels: Yup.array()
    .of(RequiredString())
    .min(1, 'Must choose at least 1'),
  otherSecondaryLanguages: OptionalString().when('secondaryLanguages', {
    is: (secondaryLanguages) => secondaryLanguages.indexOf('Other') >= 0,
    then: RequiredString(),
  }),
  preferredActivities: Yup.array()
    .of(RequiredString())
    .min(1, 'Must choose at least 1'),
  secondaryLanguages: Yup.array().of(OptionalString()),
})

/**
 * These are the actual default values supplied to the form
 * if a profile is not loaded via the network. Any missing
 * values from the network fetched profile will have these
 * values substituted as well.
 */
const blankValues: IProfessionalStepTwoFormValues = {
  cities: [] as string[],
  connectionPreference: '' as ConnectionPreference,
  gradeLevels: [] as string[],
  otherSecondaryLanguages: '',
  preferredActivities: [] as string[],
  secondaryLanguages: [] as string[],
}
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IProfessionalStepTwoFormValues>()
const RadioGroup = Input.RadioGroupRenderer<IProfessionalStepTwoFormValues>()
const CheckBoxGroup =
  Input.CheckBoxGroupRenderer<IProfessionalStepTwoFormValues>()
const ErrorSummary = Form.ErrorSummaryRenderer<IProfessionalStepTwoFormValues>()
/* END - Custom Form Renderers */

/**
 * The second step for creating an account as a business professional.
 */
const FormView: React.FC<RouteComponentProps> = () => {
  const [updateProfile, { error: mutationError }] = useUpdateProfile()

  const handleSubmitForm = async (
    values: IProfessionalStepTwoFormValues,
    actions: FormikActions<any>
  ) => {
    const result = await updateProfile({
      variables: {
        profileInput: {
          cities: values.cities as string[],
          connectionPreference:
            values.connectionPreference as ConnectionPreference,
          gradeLevels: values.gradeLevels as string[],
          id: values.id as string,
          otherSecondaryLanguages: [values.otherSecondaryLanguages] as string[],
          preferredActivities: values.preferredActivities as string[],
          secondaryLanguages: values.secondaryLanguages as string[],
        },
      },
    })
    if (
      result.data &&
      !result.data.response.success &&
      result.data.response.errors
    ) {
      result.data.response.errors
        .map((e) => e as ValidationError)
        .forEach((fieldError) =>
          actions.setFieldError(`${fieldError.path}`, fieldError.message)
        )
    } else {
      window.location.href = '/app/dashboard'
      return
    }
    actions.setSubmitting(false)
  }

  const { getCurrentProfile, loading, professional } = useCurrentProfile()
  useEffect(() => {
    getCurrentProfile()
  }, [getCurrentProfile])

  if (loading) {
    return <Indicators.Spinner />
  }

  const defaultValues =
    (professional as IProfessionalStepTwoFormValues) || blankValues
  const initialValues = Object.keys(defaultValues).reduce(
    (o, k) => ({ ...o, [k]: defaultValues[k] || o[k] }),
    blankValues
  )

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmitForm}
      validationSchema={ProfessionalStepTwoSchema}
    >
      {({
        errors,
        handleSubmit,
        isSubmitting,
        values,
        setFieldValue,
        submitCount,
        submitForm,
      }) => {
        const { connectionPreference, gradeLevels, cities } = values

        const allGrades = ['all', 'K-2', '3-5', '6-8', '9-10', '11-12']
        if (
          gradeLevels &&
          gradeLevels.includes('all') &&
          gradeLevels.length < allGrades.length
        ) {
          setFieldValue('gradeLevels', allGrades)
        }

        const allCities = [
          'all',
          'Avondale',
          'Phoenix',
          'Peoria',
          'Mesa',
          'Chandler',
          'Scottsdale',
          'Tempe',
          'Surprise',
          'Gilbert',
          'Glendale',
        ]
        if (
          cities &&
          cities.includes('all') &&
          cities.length < allCities.length
        ) {
          setFieldValue('cities', allCities)
        }

        if (
          connectionPreference === ConnectionPreference.Videoconference &&
          cities &&
          !cities.includes('all')
        ) {
          setFieldValue('cities', allCities)
        }

        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item>
                <RadioGroup
                  disabled={isSubmitting}
                  label="How Would You Like to Connect with Educators?"
                  required={true}
                  inputWidth={1}
                  name="connectionPreference"
                  values={[
                    {
                      label: 'In-Person',
                      value: ConnectionPreference.Inperson,
                    },
                    {
                      label: 'Video Conference',
                      value: ConnectionPreference.Videoconference,
                    },
                    { label: 'Both', value: ConnectionPreference.Both },
                  ]}
                />
              </Form.Item>
            </Form.Row>

            <Form.Row>
              <Form.Item>
                <CheckBoxGroup
                  required={true}
                  label="Preferred Connection Activities"
                  name="preferredActivities"
                  disabled={isSubmitting}
                  values={[
                    {
                      label: 'Share my career journey and answer questions',
                      value: 'careerJourney',
                    },
                    {
                      label:
                        'Provide content/subject consultation with a class',
                      value: 'classConsultation',
                    },
                    {
                      label:
                        'Support students with a classroom project by asking questions and linking current industry standards to the project',
                      value: 'supportClassProjpect',
                    },
                    {
                      label:
                        'Consult with a teacher on designing a real-world classroom project',
                      value: 'designClassProject',
                    },
                    {
                      label: 'Become of member of a CTE Advisory Committee',
                      value: 'becomeCTEAdvisor',
                    },
                    {
                      label: 'Participate in an exhibit or school fair',
                      value: 'participateInFair',
                    },
                    {
                      label:
                        'Participate as a judge at an event (e.g. science fair)',
                      value: 'judgeAnEvent',
                    },
                  ]}
                />
              </Form.Item>
            </Form.Row>

            <Form.Row>
              <Form.Item>
                <CheckBoxGroup
                  description="Provide support in all cities, or just nearby.  It’s up to you! Note: if you selected videoconferencing only above, all cities will be selected on your behalf."
                  disabled={
                    isSubmitting ||
                    connectionPreference ===
                      ConnectionPreference.Videoconference
                  }
                  label="Limit Connections to Specific Cities?"
                  required={true}
                  inline={true}
                  inputWidth={1 / 2}
                  name="cities"
                  allOrNothingValue="all"
                  values={[
                    { label: 'All cities', value: 'all' },
                    { label: 'Gilbert', value: 'Gilbert' },
                    { label: 'Phoenix', value: 'Phoenix' },
                    { label: 'Chandler', value: 'Chandler' },
                    { label: 'Scottsdale', value: 'Scottsdale' },
                    { label: 'Avondale', value: 'Avondale' },
                    { label: 'Mesa', value: 'Mesa' },
                    { label: 'Peoria', value: 'Peoria' },
                    { label: 'Tempe', value: 'Tempe' },
                    { label: 'Surprise', value: 'Surprise' },
                    { label: 'Glendale', value: 'Glendale' },
                  ]}
                />
              </Form.Item>
            </Form.Row>

            <Form.Row>
              <Form.Item>
                <CheckBoxGroup
                  disabled={isSubmitting}
                  label="Choose Grade Levels for Support"
                  required={true}
                  inline={true}
                  inputWidth={1 / 2}
                  name="gradeLevels"
                  allOrNothingValue="all"
                  values={[
                    { label: 'All Grades', value: 'all' },
                    { label: '6-8', value: '6-8' },
                    { label: 'K-2', value: 'K-2' },
                    { label: '9-10', value: '9-10' },
                    { label: '3-5', value: '3-5' },
                    { label: '11-12', value: '11-12' },
                  ]}
                />
              </Form.Item>
            </Form.Row>

            <Form.Row>
              <Form.Item>
                <CheckBoxGroup
                  required={false}
                  label="Secondary Language Preferences"
                  description="In addition to English, please indicate other languages in which you are fluent."
                  name="secondaryLanguages"
                  disabled={isSubmitting}
                  inline={true}
                  inputWidth={1 / 2}
                  values={[
                    { label: 'French', value: 'French' },
                    { label: 'Spanish', value: 'Spanish' },
                    { label: 'Japanese', value: 'Japanese' },
                    { label: 'Portugese', value: 'Portugese' },
                    { label: 'Latin', value: 'Latin' },
                    { label: 'Chinese', value: 'Chinese' },
                    { label: 'Italian', value: 'Italian' },
                    { label: 'Arabic', value: 'Arabic' },
                    { label: 'Hindi', value: 'Hindi' },
                    { label: 'Russian', value: 'Russian' },
                    { label: 'Other', value: 'Other' },
                  ]}
                />
                {(values.secondaryLanguages || []).includes('Other') ? (
                  <Form.AssociatedField>
                    <FormField
                      name="otherSecondaryLanguages"
                      type="text"
                      placeholder="other languages, separated by commas"
                    />
                  </Form.AssociatedField>
                ) : null}
              </Form.Item>
            </Form.Row>

            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            <Form.GeneralError name="_" />
            {submitCount > 0 ? <ErrorSummary errors={errors} /> : null}
            <Form.Row>
              <Form.Item width={1}>
                <Content.ActionButton
                  onClick={submitForm}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  label="Save and Submit"
                />
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const ProfessionalStepTwo = withRouter(FormView)
